import { allEntries, rebuildSections } from "./parser";
import { parseSource } from "./screenplay-format";
import type { EntryKind, ParseResult, ParserMode, ScriptDocument, ScriptEntry } from "./types";

export function renameCharacter(document: ScriptDocument, characterId: string, name: string): boolean {
  const character = document.characters.find((item) => item.id === characterId);
  const nextName = name.trim();
  if (!character || !nextName) return false;
  character.name = nextName;
  allEntries(document).forEach((entry) => {
    if (entry.speakerId === characterId) entry.speakerLabel = nextName;
  });
  rebuildSections(document);
  return true;
}

export function mergeCharacters(document: ScriptDocument, sourceId: string, targetId: string): boolean {
  if (sourceId === targetId) return false;
  const source = document.characters.find((item) => item.id === sourceId);
  const target = document.characters.find((item) => item.id === targetId);
  if (!source || !target) return false;
  target.aliases = [...new Set([...target.aliases, source.name, ...source.aliases])];
  target.voiceId ||= source.voiceId;
  allEntries(document).forEach((entry) => {
    if (entry.speakerId === sourceId) {
      entry.speakerId = target.id;
      entry.speakerLabel = target.name;
    }
  });
  document.characters = document.characters.filter((item) => item.id !== sourceId);
  document.settings.selectedCharacterIds = document.settings.selectedCharacterIds.map((id) => id === sourceId ? targetId : id).filter((id, index, values) => values.indexOf(id) === index);
  rebuildSections(document);
  return true;
}

export function deleteCharacter(document: ScriptDocument, characterId: string): boolean {
  if (!document.characters.some((item) => item.id === characterId)) return false;
  document.characters = document.characters.filter((item) => item.id !== characterId);
  document.settings.selectedCharacterIds = document.settings.selectedCharacterIds.filter((id) => id !== characterId);
  allEntries(document).forEach((entry) => {
    if (entry.speakerId === characterId) {
      entry.speakerId = undefined;
      entry.speakerLabel = undefined;
    }
  });
  rebuildSections(document);
  return true;
}

export function editEntry(document: ScriptDocument, entryId: string, patch: { text?: string; kind?: EntryKind; speakerId?: string }): boolean {
  const entry = allEntries(document).find((item) => item.id === entryId);
  if (!entry) return false;
  if (patch.text !== undefined) entry.text = patch.text.trim();
  if (patch.kind !== undefined) entry.kind = patch.kind;
  if ("speakerId" in patch) {
    const character = document.characters.find((item) => item.id === patch.speakerId);
    entry.speakerId = character?.id;
    entry.speakerLabel = character?.name;
  }
  rebuildSections(document);
  return true;
}

/** Reparse corrected source while retaining the script's settings and unchanged rehearsal progress. */
export function replaceScriptSource(document: ScriptDocument, sourceText: string, parserMode: ParserMode): ParseResult {
  const result = parseSource(sourceText, parserMode, document.importMode, document.title);
  const reparsed = result.document;
  const characterIds = new Map<string, string>();

  reparsed.characters = reparsed.characters.map((character) => {
    const labels = new Set([character.name, ...character.aliases].map(normalizedLabel));
    const existing = document.characters.find((candidate) => [candidate.name, ...candidate.aliases].some((label) => labels.has(normalizedLabel(label))));
    if (!existing) return character;
    characterIds.set(character.id, existing.id);
    return { ...existing, aliases: [...new Set([...existing.aliases, ...character.aliases])] };
  });

  for (const item of allEntries(reparsed)) {
    const mappedId = item.speakerId ? characterIds.get(item.speakerId) : undefined;
    if (!mappedId) continue;
    const character = reparsed.characters.find((candidate) => candidate.id === mappedId);
    item.speakerId = mappedId;
    item.speakerLabel = character?.name;
  }

  const previousEntries = new Map<string, ScriptEntry[]>();
  for (const item of allEntries(document)) {
    const key = entryIdentity(item);
    previousEntries.set(key, [...(previousEntries.get(key) ?? []), item]);
  }
  const memorisation: ScriptDocument["memorisation"] = {};
  for (const item of allEntries(reparsed)) {
    const previous = previousEntries.get(entryIdentity(item))?.shift();
    if (!previous) continue;
    item.id = previous.id;
    if (document.memorisation[previous.id]) memorisation[item.id] = document.memorisation[previous.id]!;
  }

  const retainedCharacterIds = new Set(reparsed.characters.map((character) => character.id));
  reparsed.id = document.id;
  reparsed.title = document.title;
  reparsed.settings = {
    ...document.settings,
    selectedCharacterIds: document.settings.selectedCharacterIds.filter((characterId) => retainedCharacterIds.has(characterId)),
  };
  reparsed.memorisation = memorisation;
  reparsed.tags = [...document.tags];
  reparsed.notes = document.notes;
  reparsed.folder = document.folder;
  reparsed.createdAt = document.createdAt;
  rebuildSections(reparsed);
  return { ...result, document: reparsed };
}

function normalizedLabel(value: string): string {
  return value.replace(/^@/, "").replace(/:$/, "").replace(/\s+/g, " ").trim().toLocaleUpperCase();
}

function entryIdentity(entry: ScriptEntry): string {
  return `${entry.kind}\u0000${entry.speakerId ?? ""}\u0000${entry.text}`;
}
